import { Calendar, ChevronDown, ExternalLink, Globe, Link, Map } from 'lucide-react'
import { useState } from 'react'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import { sitemapper } from 'sprites-types/workflow/tools/sitemapper'
import { ContextMenuWrapper } from '../shared/ContextMenuWrapper'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'

function SitemapUrlItem({ url, className }: { url: any; className?: string }) {
  const location = typeof url === 'string' ? url : url?.loc || url?.url || ''
  const lastModified = typeof url === 'object' ? url?.lastmod || url?.lastModified : ''
  const priority = typeof url === 'object' ? url?.priority : ''
  const changeFreq = typeof url === 'object' ? url?.changefreq || url?.frequency : ''

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleDateString()
    } catch {
      return dateStr
    }
  }

  const getDomain = (urlStr: string) => {
    try {
      return new URL(urlStr).hostname
    } catch {
      return urlStr
    }
  }

  return (
    <div
      className={`rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-sm ${className}`}
    >
      <div className="px-4 py-3">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex-shrink-0">
            <Link className="h-4 w-4 text-blue-600" />
          </div>

          <div className="min-w-0 flex-1">
            <div className="mb-1 break-all text-sm font-medium text-gray-900">{location}</div>

            <div className="mb-2 flex items-center gap-3 text-xs text-gray-600">
              <div className="flex items-center gap-1">
                <Globe className="h-3 w-3" />
                <span>{getDomain(location)}</span>
              </div>
              {priority && <div>Priority: {priority}</div>}
              {changeFreq && <div>Frequency: {changeFreq}</div>}
            </div>

            {lastModified && (
              <div className="mb-2 flex items-center gap-1 text-xs text-gray-600">
                <Calendar className="h-3 w-3" />
                <span>Last modified: {formatDate(lastModified)}</span>
              </div>
            )}

            <div className="flex items-center gap-3">
              <a
                href={location}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
              >
                <ExternalLink className="h-3 w-3" />
                Visit Page
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function SitemapperDisplayComponent({ nodeExecution }: WorkflowToolComponentProps) {
  const schema = getToolResultSchema(sitemapper, nodeExecution.inputs)
  const [isExpanded, setIsExpanded] = useState(true)

  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const result = parseResult.data
  const inputs = nodeExecution.inputs

  const siteUrl = inputs.url || inputs.siteUrl || inputs.domain || ''
  const urls = Array.isArray(result) ? result : result.urls || (result ? [result] : [])
  const totalUrls = urls.length

  if (urls.length === 0) {
    return (
      <div className="rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]">
        <div className="flex items-center gap-4 px-4 py-3">
          <div className="flex-shrink-0">
            <Map className="h-5 w-5 text-gray-400" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-gray-900">No sitemap URLs found</div>
            {siteUrl && <div className="text-xs text-gray-600">Site: {siteUrl}</div>}
          </div>
        </div>
      </div>
    )
  }

  if (urls.length === 1) {
    return <SitemapUrlItem url={urls[0]} />
  }

  return (
    <div className="space-y-3">
      <ContextMenuWrapper actions={[]}>
        <div className="overflow-hidden rounded-3xl border border-gray-200/50 bg-gray-200/70 text-black shadow-sm">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="group flex w-full items-center gap-4 bg-white px-4 py-3 text-left transition-colors hover:bg-gray-50/50"
            aria-expanded={isExpanded}
          >
            <div className="flex-shrink-0">
              <Map className="h-5 w-5 text-blue-600" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-gray-900">Sitemap URLs</div>
              <div className="text-xs font-medium tracking-wide text-gray-500">
                {totalUrls} URLs found{siteUrl ? ` • ${new URL(siteUrl).hostname}` : ''}
              </div>
            </div>
            <div className="flex-shrink-0">
              <ChevronDown
                className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                aria-hidden="true"
              />
            </div>
          </button>
        </div>
      </ContextMenuWrapper>

      {isExpanded && (
        <div className="space-y-2">
          {urls.slice(0, 50).map((url: any, index: number) => (
            <SitemapUrlItem key={index} url={url} className="mx-8" />
          ))}
          {urls.length > 50 && (
            <div className="mx-8 py-2 text-center text-xs text-gray-500">
              Showing first 50 of {urls.length} URLs
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function downloadSitemapper(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function sitemapperToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(sitemapper, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  const result = parseResult.data
  const inputs = nodeExecution.inputs

  const siteUrl = inputs.url || inputs.siteUrl || inputs.domain || ''
  const urls = Array.isArray(result) ? result : result.urls || (result ? [result] : [])

  if (urls.length === 0) {
    return `## Sitemap URLs

${siteUrl ? `**Site:** ${siteUrl}\n\n` : ''}
No URLs found in sitemap.

---
`
  }

  const urlsMarkdown = urls
    .slice(0, 100) // Limit to prevent huge markdown files
    .map((url: any) => {
      const location = typeof url === 'string' ? url : url?.loc || url?.url || ''
      const lastModified = typeof url === 'object' ? url?.lastmod || url?.lastModified : ''
      const priority = typeof url === 'object' ? url?.priority : ''

      return `- ${location}${lastModified ? ` (Modified: ${new Date(lastModified).toLocaleDateString()})` : ''}${priority ? ` (Priority: ${priority})` : ''}`
    })
    .join('\n')

  return `## Sitemap URLs (${urls.length} total)

${siteUrl ? `**Site:** ${siteUrl}\n\n` : ''}
${urlsMarkdown}
${urls.length > 100 ? `\n\n*Showing first 100 of ${urls.length} URLs*` : ''}

---
`
}

export const SitemapperDisplay = createWorkflowToolComponent(
  SitemapperDisplayComponent,
  downloadSitemapper,
  sitemapperToMarkdown
)
